import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { DriveConnections, DriveOwner, Preset, UserDriveCollaboration } from '@bpm-web-app/create-api-sdk';
import { usePlaySoundList, useUserSettings } from '@bpm-web-app/utils';
import DragSelect from 'dragselect';
import styles from '../create/track-list-create.module.css';
import { Checkbox } from '../../ui/checkbox/checkbox';
import SortArrowDown from '../../../../assets/icons/create/sort-arrow-down.svg';
import SortArrowUp from '../../../../assets/icons/create/sort-arrow-up.svg';

import { TrackListCreatePresetItem } from './track-list-create-preset-item';
import { useCreatePlayer } from '../../../../../../utils/src/lib/create-player.context';

import { CreateThreeDotsSheetContext } from '../../three-dots-sheet/create-three-dots-sheet.context';
import { LoadMore } from '../../load-more/load-more';
import SecondaryPageTitle from '../../secondary-page-title/secondary-page-title';
import { TagsView } from '../../../tags-view/tags-view';
import SeeMore from '../../see-more-button/see-more-btn';
import { TagsViewGhost } from '../../../tags-view/tags-view-ghost';
import { TrackListGhostLoading } from '../ghost-loading/track-list-ghost-loading';
import { NoResultsBlock } from '../../ui';

type CreateSortByMain = 'date' | 'title' | 'bpm' | 'relevance';

interface TrackListPresetsBaseProps {
    mutateSound?: (id: string, duration: number) => void;
    list?: Array<Preset>;
    hideHeading?: boolean;
    isLoading?: boolean;
    isLoadingMore?: boolean;
    onLoadMore?: () => void;
    automatedLoadMore?: boolean;
    hasMore?: boolean;
    hideTagColumn?: boolean;
    hidePackName?: boolean;
    isDriveDetailPage?: boolean;
    hasEditAccess?: boolean;
    title?: string;
    showTagFilter?: boolean;
    onSeeMorePressed?: () => void;
    collaborators?: UserDriveCollaboration[];
    connections?: DriveConnections[]
    owner?: DriveOwner;
    hideNotAvailableBlock?: boolean;
}

interface TrackListPresetsTagFilterProps {
    showTagFilter: true;
    tags: string[];
    selectedTags: string[];
    onToggleTag: (tag: string, toggleOn: boolean) => void;
}

interface TrackListPresetsNoTagFilterProps {
    showTagFilter?: false;
    tags?: never
    selectedTags?: never
    onToggleTag?: never
}
interface TrackListPresetsNonSelectableProps extends TrackListPresetsBaseProps {
    onSelectItem?: never;
    selectedItemsIds?: never;
    onSelectAll?: never;
}

interface TrackListPresetsSelectableProps extends TrackListPresetsBaseProps {
    /** Sets the TrackList with the selectable variant */
    onSelectItem: (soundId: string, index: number) => void;
    selectedItemsIds: string[];
    onSelectAll: (checked: boolean) => void;
}

export type TrackListCreatePresetsProps = (TrackListPresetsNonSelectableProps | TrackListPresetsSelectableProps) & (TrackListPresetsTagFilterProps | TrackListPresetsNoTagFilterProps);

export function TrackListCreatePresets({
    title,
    mutateSound,
    list: propsList,
    onSelectItem,
    selectedItemsIds,
    onSelectAll,
    hasMore,
    isLoading = false,
    isLoadingMore = false,
    hideHeading = false,
    onLoadMore,
    automatedLoadMore,
    hideTagColumn = false,
    hidePackName = false,
    isDriveDetailPage = false,
    hasEditAccess,
    showTagFilter = false,
    tags,
    selectedTags,
    onToggleTag,
    onSeeMorePressed,
    collaborators,
    connections,
    owner,
    hideNotAvailableBlock
}: TrackListCreatePresetsProps) {
    const { setCurrentSelectedSoundId, currentSelectedSoundType, currentSelectedSoundId, setCurrentSoundId } = useCreatePlayer();

    const [list, setList] = useState(propsList);
    const listRef = useRef(list);
    const [fadeOut, setFaceOut] = useState(false);
    const currentFadeoutTimer = useRef<NodeJS.Timeout>();

    const { isAnonymous, setSelectedMedia, setShowSignUpModal } = useUserSettings();

    const setCurrentMediaInContext = useCallback((id: string, showSignUpModal?: boolean) => {
        if (isAnonymous) {
            const currentCenterMedia = list?.find((item) => `${item.id}` === id);
            if (currentCenterMedia as Preset) setSelectedMedia(currentCenterMedia as Preset, list as Preset[]);

            if (showSignUpModal) {
                setShowSignUpModal({ type: 'preset' });
            }
        }
    }, [isAnonymous, list, setSelectedMedia, setShowSignUpModal]);

    useEffect(() => {
        if (currentFadeoutTimer) { clearTimeout(currentFadeoutTimer.current as unknown as number); }
        if (!propsList || propsList.length === 0) {
            setFaceOut(true);
            currentFadeoutTimer.current = setTimeout(() => {
                setFaceOut(false);
                setList(propsList);
            }, 50);
        } else {
            setFaceOut(false);
            setList(propsList);
        }
    }, [propsList]);

    useEffect(() => {
        listRef.current = list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list?.length]);

    useEffect(() => {
        setCurrentSoundId(undefined);
        setCurrentSelectedSoundId(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (propsList as Preset[] && (propsList as Preset[])[0] && (propsList as Preset[])[0].id) setCurrentMediaInContext(`${(propsList as Preset[])[0].id}`, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnonymous]);

    const dragSelect = useRef<DragSelect>();

    const playSoundListContext = usePlaySoundList('preset', list || []);
    const { play } = playSoundListContext;

    /** useEffect(() => {
        if (isQualityControlActive && onSelectItem) {
            ragSelect.current = new DragSelect({
            });
            dragSelect.current.subscribe('callback', ({ items }: CallbackObject) => {
                (items || []).forEach((e) => {
                    if (e.dataset.soundId) {
                        const soundIndex = (listRef.current || []).findIndex((s) => s.id === e.dataset.soundId);
                        if (soundIndex > -1) {
                            onSelectItem(e.dataset.soundId, soundIndex);
                        }
                    }
                });
                document.getSelection()?.empty();
                if (dragSelect.current) {
                    dragSelect.current.clearSelection(false);
                }
            });
        }
        return () => {
            dragSelect.current?.unsubscribe('callback');
            dragSelect.current?.stop(true);
        };
    }, [isQualityControlActive, onSelectItem]); * */

    const { actionTypeId } = useContext(CreateThreeDotsSheetContext);

    const handleUserKeyPress = useCallback(
        async (event) => {
            const { key } = event;

            if (actionTypeId === -1 && list) {
                if (currentSelectedSoundId !== undefined && currentSelectedSoundType === 'sound') {
                    return;
                }
                const selectedTrackIndex = list.findIndex((sound) => sound.id === currentSelectedSoundId);

                if (key === 'ArrowDown' && list.length > selectedTrackIndex + 1) {
                    event.preventDefault();
                    play(selectedTrackIndex + 1);
                } else if (key === 'ArrowUp' && selectedTrackIndex > 0) {
                    event.preventDefault();
                    play(selectedTrackIndex - 1);
                }
            }
        },
        [actionTypeId, list, currentSelectedSoundId, currentSelectedSoundType, play]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleUserKeyPress);

        return () => {
            window.removeEventListener('keydown', handleUserKeyPress);
        };
    }, [handleUserKeyPress]);

    const selectAllCheckbox = useMemo(
        () => (
            <Checkbox
                checked={!!selectedItemsIds?.length && selectedItemsIds?.length === list?.length}
                onChange={(e) => {
                    if (onSelectAll) onSelectAll(e.target.checked);
                }}
            />
        ),
        [list?.length, onSelectAll, selectedItemsIds?.length]
    );

    const { trackListSortBy, setTrackListSortBy } = useCreatePlayer();

    const toggleSortBy = useCallback(
        (sortBy: CreateSortByMain) => {
            switch (sortBy) {
                case 'relevance': {
                    setTrackListSortBy('relevance');
                    break;
                }

                default: {
                    if (trackListSortBy === `${sortBy}_asc`) {
                        setTrackListSortBy(`${sortBy}_desc`);
                        break;
                    }
                    setTrackListSortBy(`${sortBy}_asc`);
                    break;
                }
            }
        },
        [setTrackListSortBy, trackListSortBy]
    );

    const renderTrackItem = useCallback((index: number) => {
        const connection = connections?.find((c) => c.sound_id === (list![index] as Preset).id);
        const collaborator = connection?.added_by_key ? collaborators?.find((current) => current.id === connection?.added_by_key) : undefined;
        const lastItem = index === list!.length - 1;
        return (
            onSelectItem ? (
                <TrackListCreatePresetItem
                    setCurrentMediaInContext={setCurrentMediaInContext}
                    sound={list![index]}
                    updateSoundLocal={(sound) => {
                        list![index] = sound as Preset;
                        setList(list?.slice());
                    }}
                    key={list![index].id}
                    soundIndex={index}
                    onSelectItem={onSelectItem}
                    selectedItemsIds={selectedItemsIds}
                    hideTagColumn={hideTagColumn}
                    hidePackName={hidePackName}
                    isDriveDetailPage={isDriveDetailPage}
                    hasEditAccess={isDriveDetailPage || (hasEditAccess && connection?.is_own)}
                    mutateSound={mutateSound}
                    collaborator={collaborator}
                    isSharedDrive={collaborators && collaborators?.length > 0}
                    owner={owner}
                    addedAt={connection?.added_at}
                    isLastItem={lastItem}
                    dragSelect={dragSelect.current}
                    playContext={playSoundListContext}
                />
            ) : (
                <TrackListCreatePresetItem
                    setCurrentMediaInContext={setCurrentMediaInContext}
                    sound={list![index]}
                    updateSoundLocal={(sound) => {
                        list![index] = sound as Preset;
                        setList(list?.slice());
                    }}
                    key={list![index].id}
                    soundIndex={index}
                    hideTagColumn={hideTagColumn}
                    hidePackName={hidePackName}
                    isDriveDetailPage={isDriveDetailPage}
                    hasEditAccess={isDriveDetailPage || (hasEditAccess && connection?.is_own)}
                    mutateSound={mutateSound}
                    collaborator={collaborator}
                    isSharedDrive={collaborators && collaborators?.length > 0}
                    owner={owner}
                    addedAt={connection?.added_at}
                    isLastItem={lastItem}
                    dragSelect={dragSelect.current}
                    playContext={playSoundListContext}
                />
            )
        );
    }, [collaborators, connections, hasEditAccess, hidePackName, hideTagColumn, isDriveDetailPage, list,
        mutateSound, onSelectItem, owner, selectedItemsIds, setCurrentMediaInContext, playSoundListContext]);

    if (isLoading) {
        return (
            <>
                {title ? <SecondaryPageTitle noPadding title={title} /> : null}
                {showTagFilter ? (
                    <>
                        <div className="spacing--top" />
                        <TagsViewGhost large={false} key="tags-view-small-ghost" />
                    </>
                ) : null}
                <div
                    className={classNames(styles['track-list-create__list-heading'], styles['track-list-create__list-heading__preset'], {
                        [styles['track-list-create__list-heading--sortable']]: false,
                    })}
                >
                    <div className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--count'])}>{onSelectItem ? selectAllCheckbox : null}</div>
                    <button
                        type="button"
                        aria-label="sort by title"
                        className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--title'])}
                        onClick={() => toggleSortBy('title')}
                    >
                        Title
                        {trackListSortBy === 'title_asc' ? <SortArrowUp /> : trackListSortBy === 'title_desc' && <SortArrowDown />}
                    </button>
                    <div className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--synth'])}>
                        Synth
                    </div>
                    <div className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--genre'])}>
                        Genre
                    </div>
                </div>
                <div className="spacing--top" />
                <TrackListGhostLoading amount={10} />
            </>
        );
    }

    return (
        <div className={classNames(styles['track-list-create'])}>
            {title && <SecondaryPageTitle title={title} noPadding />}
            {showTagFilter && (
                <div className={classNames(styles['track-list-create__tag-view'])}>
                    <TagsView tags={tags!} selected={selectedTags!} onToggleTag={onToggleTag!} />
                </div>
            )}
            {hideHeading ? null : (
                <div
                    className={classNames(styles['track-list-create__list-heading'], styles['track-list-create__list-heading__preset'], {
                        [styles['track-list-create__list-heading--sortable']]: false,
                    })}
                >
                    <div className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--count'])}>{onSelectItem ? selectAllCheckbox : null}</div>
                    <button
                        type="button"
                        aria-label="sort by title"
                        className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--title'])}
                        onClick={() => toggleSortBy('title')}
                    >
                        Title
                        {trackListSortBy === 'title_asc' ? <SortArrowUp /> : trackListSortBy === 'title_desc' && <SortArrowDown />}
                    </button>
                    <div className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--synth'])}>
                        Synth
                    </div>
                    <div className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--genre'])}>
                        Genre
                    </div>
                    {!hideTagColumn && <div className={classNames(styles['track-list-create__heading-column'], styles['track-list-create__heading-column--tags'])}>Tags</div>}
                </div>
            )}
            {isLoading === false && list && list?.length > 0 ? (
                <>
                    <div className={classNames(styles['track-list-create__list'], { [styles['track-list-create__list--fadeOut']]: fadeOut })}>
                        {list?.map((sound, soundIndex) => {
                            return renderTrackItem(soundIndex);
                        })}
                        {!automatedLoadMore && hasMore && onLoadMore && (
                            <button type="button" disabled={isLoadingMore} onClick={onLoadMore} className="button button--plain-link">
                                See More
                            </button>
                        )}
                        {automatedLoadMore && onLoadMore && <LoadMore onLoadMore={onLoadMore} isLoadingMore={isLoadingMore} />}
                    </div>

                    {onSeeMorePressed ? <SeeMore onClick={onSeeMorePressed} expand={false} variant="text" /> : null}
                </>
            )
                : hideNotAvailableBlock ? null : (
                    <NoResultsBlock>{title ? `No ${title} Available` : 'Not Available'}</NoResultsBlock>
                )}
        </div>
    );
}

export default TrackListCreatePresets;
